import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  FlatList,
  Text,
  StyleSheet,
  Animated,
  Keyboard,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  setQuery,
  clearQuery,
  fetchIntelligentSuggestions,
  markSuggestionUsed,
  selectIntelligentSearchQuery,
  selectIntelligentSuggestions,
  selectIntelligentSearchLoading,
  selectProcessedQuery,
  selectSearchIntent,
} from '../../store/slices/intelligentSearchSlice';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { AIExplanationDisplay } from './AIExplanationDisplay';
import type {
  IntelligentSearchBarProps,
  IntelligentSuggestion,
} from '@coffeeplexity/shared';

interface SuggestionItemProps {
  suggestion: IntelligentSuggestion;
  onSelect: (suggestion: IntelligentSuggestion) => void;
  onPress: () => void;
}

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  suggestion,
  onSelect,
  onPress,
}) => {
  const getSuggestionIcon = (type: IntelligentSuggestion['type']) => {
    switch (type) {
      case 'query_completion':
        return 'search-outline';
      case 'skill_based':
        return 'school-outline';
      case 'trending':
        return 'trending-up-outline';
      case 'personalized':
        return 'person-outline';
      case 'contextual':
        return 'bulb-outline';
      default:
        return 'search-outline';
    }
  };

  const getSuggestionColor = (type: IntelligentSuggestion['type']) => {
    switch (type) {
      case 'skill_based':
        return '#4CAF50';
      case 'trending':
        return '#FF9800';
      case 'personalized':
        return '#2196F3';
      case 'contextual':
        return '#9C27B0';
      default:
        return '#666';
    }
  };

  return (
    <TouchableOpacity
      style={styles.suggestionItem}
      onPress={() => {
        onSelect(suggestion);
        onPress();
      }}
      activeOpacity={0.7}
    >
      <View style={styles.suggestionContent}>
        <View style={styles.suggestionHeader}>
          <Ionicons
            name={getSuggestionIcon(suggestion.type)}
            size={16}
            color={getSuggestionColor(suggestion.type)}
            style={styles.suggestionIcon}
          />
          <Text style={styles.suggestionText} numberOfLines={1}>
            {suggestion.displayText}
          </Text>
          <ConfidenceIndicator
            confidence={suggestion.confidence}
            size='small'
            showLabel={false}
          />
        </View>

        {suggestion.reasoning && (
          <Text style={styles.suggestionReasoning} numberOfLines={2}>
            {suggestion.reasoning}
          </Text>
        )}

        {suggestion.metadata && (
          <View style={styles.suggestionMetadata}>
            {suggestion.metadata.skillAlignment && (
              <View style={styles.metadataItem}>
                <Ionicons name='school-outline' size={12} color='#666' />
                <Text style={styles.metadataText}>
                  {Math.round(suggestion.metadata.skillAlignment * 100)}% skill
                  match
                </Text>
              </View>
            )}
            {suggestion.metadata.popularityScore && (
              <View style={styles.metadataItem}>
                <Ionicons name='people-outline' size={12} color='#666' />
                <Text style={styles.metadataText}>
                  Popular (
                  {Math.round(suggestion.metadata.popularityScore * 100)})
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

export const IntelligentSearchBar: React.FC<IntelligentSearchBarProps> = ({
  onSearch,
  onSuggestionSelect,
  placeholder = 'Search for recipes, techniques, or ask questions...',
  autoFocus = false,
  showSuggestions = true,
  maxSuggestions = 5,
  debounceMs = 300,
}) => {
  const dispatch = useAppDispatch();
  const query = useAppSelector(selectIntelligentSearchQuery);
  const suggestions = useAppSelector(selectIntelligentSuggestions);
  const loading = useAppSelector(selectIntelligentSearchLoading);
  const processedQuery = useAppSelector(selectProcessedQuery);
  const searchIntent = useAppSelector(selectSearchIntent);

  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestionsList, setShowSuggestionsList] = useState(false);
  const [localQuery, setLocalQuery] = useState(query);

  const inputRef = useRef<TextInput>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const suggestionListHeight = useRef(new Animated.Value(0)).current;

  // Debounced suggestion fetching
  const fetchSuggestions = useCallback(
    (searchQuery: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        if (searchQuery.trim().length >= 2) {
          dispatch(
            fetchIntelligentSuggestions({
              query: searchQuery,
              userId: 'current-user', // TODO: Get from auth state
              maxSuggestions,
            })
          );
        }
      }, debounceMs);
    },
    [dispatch, maxSuggestions, debounceMs]
  );

  // Handle query changes
  useEffect(() => {
    dispatch(setQuery(localQuery));

    if (showSuggestions && localQuery.trim().length >= 2) {
      fetchSuggestions(localQuery);
    }
  }, [localQuery, dispatch, fetchSuggestions, showSuggestions]);

  // Animate suggestions list
  useEffect(() => {
    Animated.timing(suggestionListHeight, {
      toValue: showSuggestionsList && suggestions.length > 0 ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [showSuggestionsList, suggestions.length, suggestionListHeight]);

  // Handle keyboard events
  useEffect(() => {
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        setShowSuggestionsList(false);
      }
    );

    return () => {
      keyboardDidHideListener.remove();
    };
  }, []);

  const handleFocus = () => {
    setIsFocused(true);
    if (showSuggestions && suggestions.length > 0) {
      setShowSuggestionsList(true);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Delay hiding suggestions to allow for selection
    setTimeout(() => {
      setShowSuggestionsList(false);
    }, 150);
  };

  const handleSearch = () => {
    if (localQuery.trim()) {
      onSearch(localQuery.trim());
      setShowSuggestionsList(false);
      inputRef.current?.blur();
    }
  };

  const handleSuggestionSelect = (suggestion: IntelligentSuggestion) => {
    dispatch(markSuggestionUsed(suggestion.id));
    setLocalQuery(suggestion.text);
    onSuggestionSelect(suggestion);
    setShowSuggestionsList(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setLocalQuery('');
    dispatch(clearQuery());
    setShowSuggestionsList(false);
    inputRef.current?.focus();
  };

  const renderSuggestion = ({ item }: { item: IntelligentSuggestion }) => (
    <SuggestionItem
      suggestion={item}
      onSelect={handleSuggestionSelect}
      onPress={() => setShowSuggestionsList(false)}
    />
  );

  return (
    <View style={styles.container}>
      <View style={[styles.searchBar, isFocused && styles.searchBarFocused]}>
        <Ionicons
          name='search-outline'
          size={20}
          color={isFocused ? '#2196F3' : '#666'}
          style={styles.searchIcon}
        />

        <TextInput
          ref={inputRef}
          style={styles.textInput}
          value={localQuery}
          onChangeText={setLocalQuery}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onSubmitEditing={handleSearch}
          placeholder={placeholder}
          placeholderTextColor='#999'
          autoFocus={autoFocus}
          returnKeyType='search'
          autoCapitalize='none'
          autoCorrect={false}
          clearButtonMode='never'
        />

        {loading.suggestions && (
          <ActivityIndicator
            size='small'
            color='#2196F3'
            style={styles.loadingIndicator}
          />
        )}

        {localQuery.length > 0 && !loading.suggestions && (
          <TouchableOpacity
            onPress={handleClear}
            style={styles.clearButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name='close-circle' size={20} color='#999' />
          </TouchableOpacity>
        )}

        <TouchableOpacity
          onPress={handleSearch}
          style={styles.searchButton}
          disabled={!localQuery.trim()}
        >
          <Ionicons
            name='arrow-forward'
            size={20}
            color={localQuery.trim() ? '#2196F3' : '#ccc'}
          />
        </TouchableOpacity>
      </View>

      {/* Search Intent Display */}
      {processedQuery.confidence > 0.7 && searchIntent.primaryIntent && (
        <View style={styles.intentDisplay}>
          <AIExplanationDisplay
            explanation={{
              title: `Intent: ${searchIntent.primaryIntent}`,
              description:
                searchIntent.userGoal ||
                `Looking to ${searchIntent.primaryIntent}`,
              confidence: searchIntent.confidence,
              factors: searchIntent.contextualFactors,
            }}
            compact
          />
        </View>
      )}

      {/* Suggestions List */}
      {showSuggestions && (
        <Animated.View
          style={[
            styles.suggestionsContainer,
            {
              maxHeight: suggestionListHeight.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300],
              }),
              opacity: suggestionListHeight,
            },
          ]}
        >
          {showSuggestionsList && suggestions.length > 0 && (
            <FlatList
              data={suggestions.slice(0, maxSuggestions)}
              renderItem={renderSuggestion}
              keyExtractor={item => item.id}
              style={styles.suggestionsList}
              keyboardShouldPersistTaps='handled'
              showsVerticalScrollIndicator={false}
            />
          )}
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 1000,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 25,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  searchBarFocused: {
    backgroundColor: '#fff',
    borderColor: '#2196F3',
    shadowColor: '#2196F3',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  searchIcon: {
    marginRight: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 0,
  },
  loadingIndicator: {
    marginHorizontal: 8,
  },
  clearButton: {
    marginHorizontal: 8,
  },
  searchButton: {
    marginLeft: 8,
  },
  intentDisplay: {
    marginTop: 8,
    marginHorizontal: 4,
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 12,
    marginTop: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    overflow: 'hidden',
  },
  suggestionsList: {
    maxHeight: 300,
  },
  suggestionItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  suggestionContent: {
    flex: 1,
  },
  suggestionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  suggestionIcon: {
    marginRight: 8,
  },
  suggestionText: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    fontWeight: '500',
  },
  suggestionReasoning: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
    marginLeft: 24,
  },
  suggestionMetadata: {
    flexDirection: 'row',
    marginTop: 6,
    marginLeft: 24,
  },
  metadataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  metadataText: {
    fontSize: 11,
    color: '#666',
    marginLeft: 4,
  },
});

export default IntelligentSearchBar;
